/**
 * ============================================================================
 * SupplierWorkbench — Phase 6d frontend (Supplier Management Module).
 * Owner: Claude 3 (Frontend & UX).
 * Route: /suppliers
 * Purpose: top-level Procurement-Officer + QA workbench. Four hero KPI tiles
 *          headline the page, a four-axis filter bar below, then the supplier
 *          table. "Add Supplier" opens a dialog that creates PENDING_REQUEST
 *          and warns on fuzzy-name dedup (SUP-IMP-002).
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Empty state has a primary CTA + governance copy.
 *   - Loading: skeleton rows (not a spinner-only).
 *   - Error: refined envelope with Retry button.
 *
 * URS rows: SUP-001..006, SUP-IMP-002 (fuzzy dedup).
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './SupplierWorkbench.module.css';
import { WorkbenchStats } from './WorkbenchStats';
import { WorkbenchFilters } from './WorkbenchFilters';
import { SupplierRow } from './SupplierRow';
import { AddSupplierDialog } from './AddSupplierDialog';
import {
  useSuppliersQuery,
  type SuppliersQueryParams,
  type SupplierSummary,
} from './useSuppliersQuery';

export interface SupplierWorkbenchProps {
  /** Optional callback so the host shell can route to the detail page. */
  onOpenSupplier?: (id: string) => void;
}

export function SupplierWorkbench({ onOpenSupplier }: SupplierWorkbenchProps = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<SuppliersQueryParams>({});
  const [showAdd, setShowAdd] = useState(false);

  const suppliersQ = useSuppliersQuery(filters);
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const items: SupplierSummary[] = suppliersQ.data?.items ?? [];
  const stats = suppliersQ.data?.stats;
  const isEmpty =
    !suppliersQ.isLoading && !suppliersQ.isError && items.length === 0;

  function handleOpen(s: SupplierSummary) {
    if (onOpenSupplier) {
      onOpenSupplier(s.supplier_id);
      return;
    }
    // Standalone (no host callback): route to the detail page via the router.
    navigate({ to: '/suppliers/$id', params: { id: s.supplier_id } });
  }

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('supplier.workbench.eyebrow', { defaultValue: 'Suppliers' })}
        title={t('supplier.workbench.title', { defaultValue: 'Supplier workbench' })}
        subtitle={t('supplier.workbench.subtitle', {
          defaultValue:
            'Procurement and QA view of qualified suppliers, risk posture, and pending actions.',
        })}
        actions={
          <Button
            variant="primary"
            leadingIcon="+"
            onClick={() => setShowAdd(true)}
            aria-label={t('supplier.workbench.actions.addAria', {
              defaultValue: 'Add a new supplier',
            })}
          >
            {t('supplier.workbench.actions.add', {
              defaultValue: 'Add supplier',
            })}
          </Button>
        }
      />

      <WorkbenchStats stats={stats} isLoading={suppliersQ.isLoading} />

      <Card>
        <CardHeader>
          <div className={styles.cardHeaderRow}>
            <h2 className={wstyles.sectionTitle}>
              {t('supplier.workbench.list.title', { defaultValue: 'Suppliers' })}{' '}
              <span className={styles.muted}>
                ({nf.format(items.length)})
              </span>
            </h2>
            <WorkbenchFilters value={filters} onChange={setFilters} />
          </div>
        </CardHeader>
        <CardBody>
          {suppliersQ.isLoading && (
            <div
              role="status"
              aria-live="polite"
              aria-label={t('common.loading', { defaultValue: 'Loading…' })}
            >
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className={styles.skeletonRow} aria-hidden="true" />
              ))}
            </div>
          )}

          {suppliersQ.isError && (
            <div className={styles.errorBanner} role="alert">
              <span>
                {t('supplier.workbench.errors.loadFailed', {
                  defaultValue: 'Could not load the supplier list.',
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => suppliersQ.refetch()}
              >
                {t('supplier.workbench.actions.retry', {
                  defaultValue: 'Retry',
                })}
              </Button>
            </div>
          )}

          {isEmpty && (
            <div className={styles.emptyState}>
              <h3 className={styles.emptyTitle}>
                {t('supplier.workbench.empty.title', {
                  defaultValue: 'No suppliers match your filters.',
                })}
              </h3>
              <p>
                {t('supplier.workbench.empty.body', {
                  defaultValue:
                    'Clear the filters or add a new supplier to begin qualification.',
                })}
              </p>
              <Button
                variant="primary"
                leadingIcon="+"
                onClick={() => setShowAdd(true)}
              >
                {t('supplier.workbench.actions.add', {
                  defaultValue: 'Add supplier',
                })}
              </Button>
            </div>
          )}

          {!suppliersQ.isLoading && !suppliersQ.isError && items.length > 0 && (
            <table
              className={wstyles.table}
              aria-label={t('supplier.workbench.tableAria', {
                defaultValue: 'Suppliers',
              })}
            >
              <caption className={styles.srOnly}>
                {t('supplier.workbench.tableCaption', {
                  defaultValue:
                    'Supplier list with criticality, subtype, lifecycle status, risk score, and next requalification date.',
                })}
              </caption>
              <thead>
                <tr>
                  <th scope="col">
                    {t('supplier.workbench.columns.name', {
                      defaultValue: 'Supplier',
                    })}
                  </th>
                  <th scope="col">
                    {t('supplier.workbench.columns.criticality', {
                      defaultValue: 'Criticality',
                    })}
                  </th>
                  <th scope="col">
                    {t('supplier.workbench.columns.subtype', {
                      defaultValue: 'Subtype',
                    })}
                  </th>
                  <th scope="col">
                    {t('supplier.workbench.columns.status', {
                      defaultValue: 'Status',
                    })}
                  </th>
                  <th scope="col">
                    {t('supplier.workbench.columns.risk', {
                      defaultValue: 'Risk',
                    })}
                  </th>
                  <th scope="col">
                    {t('supplier.workbench.columns.nextRequal', {
                      defaultValue: 'Next requalification',
                    })}
                  </th>
                  <th scope="col">
                    {t('supplier.workbench.columns.actions', {
                      defaultValue: 'Actions',
                    })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <SupplierRow
                    key={s.supplier_id}
                    supplier={s}
                    onOpen={handleOpen}
                  />
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {showAdd && (
        <AddSupplierDialog
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            suppliersQ.refetch();
          }}
        />
      )}
    </div>
  );
}
